import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Calendar, Clock, User, Phone, ExternalLink } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { clinicInfo } from "../mock";
import useSeo from "../hooks/useSeo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function PostDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useSeo({
    title: post ? `${post.title} | Beaudesert General Practice` : "Health Article | Beaudesert General Practice",
    description: post?.excerpt || "Health tips and practice updates from Beaudesert General Practice.",
    path: `/news/${slug}`,
    image: post?.cover,
    type: "article"
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    setError(false);
    axios
      .get(`${API}/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));

    axios
      .get(`${API}/posts`)
      .then((r) => {
        if (Array.isArray(r.data)) {
          setRelated(r.data.filter((p) => p.slug !== slug).slice(0, 3));
        }
      })
      .catch(() => {});
  }, [slug]);

  return (
    <div className="relative bg-[#f0edfa] min-h-screen" style={{ overflowX: "clip" }}>
      <Navbar />

      <section className="relative px-4 md:px-8 pt-10 pb-10 md:pt-14 molecule-bg">
        <div className="mx-auto max-w-[900px]">
          <Link to="/news" className="inline-flex items-center gap-2 text-[#1a2464] font-semibold text-sm mb-6 hover:text-[#3dc9b3]">
            <ArrowLeft className="w-4 h-4" /> All articles
          </Link>

          {loading && <div className="h-64 rounded-3xl bg-white/60 animate-pulse" />}

          {error && !loading && (
            <div className="bg-white rounded-3xl p-10 text-center">
              <h1 className="section-title">Article not found</h1>
              <Link to="/news" className="inline-block mt-6 bg-[#1a2464] text-white font-semibold px-6 py-3 rounded-full">Back to news</Link>
            </div>
          )}

          {post && !loading && (
            <article>
              <div className="flex items-center gap-2 mb-4">
                <span className="inline-flex bg-[#3dc9b3]/15 text-[#1a2464] font-semibold text-[11px] px-3 py-1 rounded-full">{post.category}</span>
              </div>
              <h1 className="section-title">{post.title}</h1>
              <div className="mt-5 flex flex-wrap items-center gap-4 text-[#6b6b85] text-sm">
                <span className="inline-flex items-center gap-1.5"><User className="w-4 h-4" /> {post.author}</span>
                <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {formatDate(post.published_at)}</span>
                <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {post.read_time}</span>
              </div>

              <div className="mt-8 rounded-3xl overflow-hidden aspect-[16/9] bg-[#eae6f6]">
                <img src={post.cover} alt={post.title} className="w-full h-full object-cover" />
              </div>

              <div className="mt-10 prose-content">
                {post.body?.map((block, i) => {
                  if (block.type === "h2") {
                    return (
                      <h2 key={i} className="text-2xl md:text-3xl font-bold text-[#1a2464] mt-10 mb-4 tracking-tight">
                        {block.text}
                      </h2>
                    );
                  }
                  return (
                    <p key={i} className="text-[#1a1a2e] text-[16px] leading-[1.85] mb-5">
                      {block.text}
                    </p>
                  );
                })}
              </div>

              {/* CTA */}
              <div className="mt-12 relative rounded-[28px] bg-[#1a2464] text-white overflow-hidden p-8 md:p-10">
                <div className="absolute inset-0 wave-pattern opacity-60 pointer-events-none" />
                <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center">
                  <div>
                    <div className="text-lg md:text-xl font-bold">Have questions or want to book?</div>
                    <p className="text-white/75 text-sm mt-1">100% bulk billed with a valid Medicare card.</p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={clinicInfo.hotdocUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex items-center gap-2 bg-[#3dc9b3] text-white font-semibold pl-5 pr-2 py-2 rounded-full hover:bg-[#34b39f]"
                    >
                      Book on HotDoc
                      <span className="w-8 h-8 rounded-full bg-white/20 grid place-items-center group-hover:translate-x-0.5 transition-transform">
                        <ArrowRight className="w-4 h-4 text-white" />
                      </span>
                      <ExternalLink className="w-3.5 h-3.5 text-white/70" />
                    </a>
                    <a
                      href={`tel:${clinicInfo.phoneRaw}`}
                      className="inline-flex items-center gap-2 bg-white text-[#1a2464] font-semibold px-5 py-3 rounded-full hover:bg-white/90"
                    >
                      <Phone className="w-4 h-4" /> {clinicInfo.phone}
                    </a>
                  </div>
                </div>
              </div>
            </article>
          )}
        </div>
      </section>

      {/* Related posts */}
      {related.length > 0 && (
        <section className="px-4 md:px-8 pb-20">
          <div className="mx-auto max-w-[1200px]">
            <div className="mb-8">
              <span className="eyebrow">Keep reading</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a2464] tracking-tight">More from our health blog</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/news/${p.slug}`}
                  className="group bg-white rounded-3xl overflow-hidden border border-[#eee6f7] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-[#eae6f6]">
                    <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  </div>
                  <div className="p-6">
                    <span className="inline-flex bg-[#3dc9b3]/15 text-[#1a2464] font-semibold text-[11px] px-3 py-1 rounded-full mb-3">{p.category}</span>
                    <h3 className="font-bold text-[#1a2464] leading-snug mb-2 group-hover:text-[#3dc9b3] transition-colors">{p.title}</h3>
                    <p className="text-[#4a4a63] text-sm leading-relaxed line-clamp-2">{p.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
